import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { SectionCard, StatusPill, useDelayedFlag } from '../../components/ui';
import { mix, sbg } from '../../theme/sbgTokens';
import { formatDate, formatPercent } from '../../utils/format';
import type { AllocationPace } from '../../utils/timeOffUsage';
import { hrs, PACE_META } from './meta';

/** Usage segments read light-to-dark: what's happened is darkest, pending is a status tint. */
const FILL = {
  used: sbg.chart.seq[5],
  scheduled: sbg.chart.seq[3],
  pending: mix(sbg.status.warning.icon, 55, sbg.bg.surface),
};

/** The card lays out by its own width, not the window's: the sidebar changes how wide it is. */
const CARD_CONTAINER = 'balances';
const HERO_WIDE = `@container ${CARD_CONTAINER} (min-width: 760px)`;
const PANELS_WIDE = `@container ${CARD_CONTAINER} (min-width: 640px)`;
const HERO_COLUMNS = { gridTemplateColumns: '1fr', [HERO_WIDE]: { gridTemplateColumns: 'minmax(280px, 5fr) minmax(0, 7fr)' } };
const PANEL_COLUMNS = { gridTemplateColumns: '1fr', [PANELS_WIDE]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } };

/** Stacked usage bar against the allotment, with a marker at today's pace point. */
const UsageBar: React.FC<{ pace: AllocationPace; title: string; size?: 'md' | 'lg' }> = ({ pace, title, size = 'md' }) => {
  const usedWidth = pace.allotment > 0 ? Math.min(pace.usedPercent, 100) : pace.used > 0 ? 100 : 0;
  const scheduledWidth = pace.allotment > 0
    ? Math.min(pace.scheduledPercent, Math.max(100 - usedWidth, 0))
    : pace.used === 0 && pace.scheduled > 0 ? 100 : 0;
  const pendingWidth = pace.allotment > 0
    ? Math.min(pace.pendingPercent, Math.max(100 - usedWidth - scheduledWidth, 0))
    : pace.used === 0 && pace.scheduled === 0 && pace.pending > 0 ? 100 : 0;
  const segments = [
    { key: 'used', width: usedWidth, color: FILL.used },
    { key: 'scheduled', width: scheduledWidth, color: FILL.scheduled },
    { key: 'pending', width: pendingWidth, color: FILL.pending },
  ].filter((segment) => segment.width > 0);
  const paceNote = pace.allotment > 0 ? `; the pace point for today is at ${formatPercent(pace.elapsedPercent)}` : '';

  return (
    <Box sx={{ position: 'relative', py: '4px' }}>
      <Box
        role="img"
        aria-label={`${title}: ${hrs(pace.used)} used, ${hrs(pace.scheduled)} scheduled, and ${hrs(pace.pending)} pending out of ${hrs(pace.allotment)}${paceNote}`}
        sx={{
          display: 'flex',
          gap: '2px',
          height: size === 'lg' ? 12 : 8,
          borderRadius: sbg.radius.pill,
          bgcolor: sbg.bg.track,
          overflow: 'hidden',
        }}
      >
        {segments.map((segment) => (
          <Box key={segment.key} sx={{ width: `${segment.width}%`, flex: 'none', bgcolor: segment.color, minWidth: 2 }} />
        ))}
      </Box>
      {pace.allotment > 0 && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `calc(${pace.elapsedPercent}% - 1px)`,
            width: 2,
            borderRadius: '1px',
            bgcolor: sbg.text.primary,
            boxShadow: `0 0 0 1px ${sbg.bg.surface}`,
          }}
        />
      )}
    </Box>
  );
};

/** The headline for one balance: how far over, or how much is left, with what it is measured against. */
const balanceFigure = (pace: AllocationPace, allotmentName: string) => {
  if (pace.allotment <= 0) return { figure: hrs(pace.committed), caption: 'approved, with no allotment this year' };
  if (pace.available < 0) {
    return { figure: `${hrs(-pace.available)} over`, caption: `the ${hrs(pace.allotment)} ${allotmentName}, counting scheduled time` };
  }
  return { figure: `${hrs(pace.available)} left`, caption: `of the ${hrs(pace.allotment)} ${allotmentName}, after approved time` };
};

/** Where the year-end projection lands against the allotment: "80.9 hrs over the allotment at this pace". */
const projectionVsAllotment = (pace: AllocationPace) => {
  if (pace.allotment <= 0) return null;
  const gap = Math.round((pace.projected - pace.allotment) * 10) / 10;
  if (gap > 0) return `${hrs(gap)} over the allotment at this pace`;
  if (gap < 0) return `${hrs(-gap)} under the allotment at this pace`;
  return 'Right at the allotment at this pace';
};

/** Hours a person would have used by today if they spent the allotment evenly across the year. */
const pacePointHours = (pace: AllocationPace) => (pace.allotment * pace.elapsedPercent) / 100;

/** A status pill for the Lift surface: Navy-tinted with white text, a status-colored dot, same words as everywhere else. */
const OnBrandStatus: React.FC<{ status: AllocationPace['status'] }> = ({ status }) => {
  const meta = PACE_META[status];
  const dot = meta.tone === 'danger' ? sbg.status.danger.icon : meta.tone === 'warning' ? sbg.status.warning.icon : sbg.text.onBrand;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifySelf: 'start',
        gap: 0.75,
        height: 24,
        px: 1.25,
        borderRadius: sbg.radius.pill,
        bgcolor: 'rgba(0, 43, 96, 0.32)',
        color: sbg.text.onBrand,
        font: 'var(--sbg-type-label-sm)',
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot, boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.9)' }} />
      {meta.label}
    </Box>
  );
};

/**
 * PTO and sick time together: the balance managers and HR read first. The figure sits on the
 * screen's one Featured (Lift) surface; the bar stays on white, because gradients never sit
 * behind data (book §6.7, §11.8).
 */
const CombinedBalance: React.FC<{ pace: AllocationPace; yearNote: string }> = ({ pace, yearNote }) => {
  const meta = PACE_META[pace.status];
  const { figure, caption } = balanceFigure(pace, 'PTO and sick allotment');
  const projection = projectionVsAllotment(pace);

  return (
    <Box
      component="section"
      aria-label="PTO and sick time combined"
      sx={{ display: 'grid', gap: 3, alignItems: 'stretch', ...HERO_COLUMNS }}
    >
      <Box
        sx={{
          display: 'grid',
          alignContent: 'start',
          gap: 1.5,
          p: 3,
          borderRadius: sbg.radius.panel,
          background: sbg.gradient.lift,
          color: sbg.text.onBrand,
          minWidth: 0,
        }}
      >
        <Typography variant="overline" component="h3" sx={{ color: sbg.text.onBrand }}>
          PTO + sick, combined
        </Typography>
        <Box>
          <Typography variant="numericXl" component="p" sx={{ color: sbg.text.onBrand, overflowWrap: 'anywhere' }}>
            {figure}
          </Typography>
          {/* Small white text stays in the Lift's left half, where it passes AA. */}
          <Typography variant="body2" component="p" sx={{ color: sbg.text.onBrand, maxWidth: 300 }}>
            {caption}
          </Typography>
        </Box>
        {pace.status !== 'within-pace' && <OnBrandStatus status={pace.status} />}
        <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.28)', pt: 1.5, display: 'grid', gap: 0.25 }}>
          <Typography variant="overline" component="p" sx={{ color: sbg.text.onBrand }}>
            Projected by Dec 31
          </Typography>
          <Typography variant="numericMd" component="p" sx={{ color: sbg.text.onBrand }}>
            {hrs(pace.projected)}
          </Typography>
          {projection && (
            <Typography variant="body2" component="p" sx={{ color: sbg.text.onBrand, maxWidth: 300 }}>
              {projection}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', alignContent: 'center', gap: 2, minWidth: 0, [HERO_WIDE]: { py: 1 } }}>
        <Box sx={{ display: 'grid', gap: 0.5 }}>
          <Typography variant="h4" component="p" sx={{ color: sbg.text.primary }}>
            {meta.detail}
          </Typography>
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
            {yearNote}
          </Typography>
        </Box>
        <UsageBar pace={pace} title="PTO and sick time combined" size="lg" />
        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(112px, 1fr))', gap: 1.5 }}>
          {([
            { label: 'Used', value: pace.used, marker: <LegendSwatch color={FILL.used} /> },
            { label: 'Scheduled', value: pace.scheduled, marker: <LegendSwatch color={FILL.scheduled} /> },
            { label: 'Pending', value: pace.pending, marker: <LegendSwatch color={FILL.pending} /> },
            ...(pace.allotment > 0 ? [{ label: 'Pace point today', value: pacePointHours(pace), marker: <PaceMarkerSwatch /> }] : []),
          ]).map(({ label, value, marker }) => (
            <Box key={label} sx={{ minWidth: 0 }}>
              <Typography component="dt" variant="caption" sx={{ color: sbg.text.secondary, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {marker}
                {label}
              </Typography>
              <Typography
                component="dd"
                variant="h4"
                sx={{
                  m: 0,
                  fontVariantNumeric: 'tabular-nums',
                  color: label === 'Pending' && value > 0 ? sbg.status.warning.text : sbg.text.primary,
                }}
              >
                {hrs(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const LegendSwatch: React.FC<{ color: string }> = ({ color }) => (
  <Box component="span" aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flex: 'none' }} />
);

const PaceMarkerSwatch: React.FC = () => (
  <Box component="span" aria-hidden="true" sx={{ width: 2, height: 12, mx: '4px', borderRadius: '1px', bgcolor: sbg.text.primary, flex: 'none' }} />
);

/** One category. PTO and sick time sit side by side under the combined balance, at a smaller size. */
const BalancePanel: React.FC<{ title: string; pace: AllocationPace }> = ({ title, pace }) => {
  const meta = PACE_META[pace.status];
  const { figure, caption } = balanceFigure(pace, 'allotment');

  return (
    <Box
      component="section"
      aria-label={title}
      sx={{ borderTop: `1px solid ${sbg.border.default}`, pt: 2, display: 'grid', gap: 1.25, alignContent: 'start', minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 22 }}>
        <Typography variant="overline" component="h3" sx={{ color: sbg.text.accent }}>
          {title}
        </Typography>
        {meta.tone && <StatusPill tone={meta.tone}>{meta.label}</StatusPill>}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1 }}>
        <Typography variant="numericMd" component="p" sx={{ color: sbg.text.primary }}>
          {figure}
        </Typography>
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
          {caption}
        </Typography>
      </Box>

      <UsageBar pace={pace} title={title} />

      <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
        {[
          `${hrs(pace.used)} used`,
          `${hrs(pace.scheduled)} scheduled`,
          `${hrs(pace.pending)} pending`,
        ].join(' · ')}
        {' · '}
        Projected by Dec 31:{' '}
        <Box component="span" sx={{ color: sbg.text.primary }}>
          {hrs(pace.projected)}
        </Box>
      </Typography>
    </Box>
  );
};

/** Skeletons at the real layout's size, so balances don't jump when they arrive. */
const useSkeletonBlock = () => {
  const visible = useDelayedFlag(true, 300);
  return (width: string | number, height: number, sx?: object) => (
    <Skeleton variant="rounded" width={width} height={height} sx={{ visibility: visible ? 'visible' : 'hidden', ...sx }} />
  );
};

const CombinedSkeleton: React.FC = () => {
  const block = useSkeletonBlock();
  return (
    <Box aria-hidden="true" sx={{ display: 'grid', gap: 3, ...HERO_COLUMNS }}>
      {block('100%', 236, { borderRadius: '20px' })}
      <Box sx={{ display: 'grid', alignContent: 'center', gap: 2 }}>
        {block('70%', 22)}
        {block('100%', 12)}
        {block('90%', 40)}
      </Box>
    </Box>
  );
};

const PanelSkeleton: React.FC = () => {
  const block = useSkeletonBlock();
  return (
    <Box aria-hidden="true" sx={{ borderTop: `1px solid ${sbg.border.default}`, pt: 2, display: 'grid', gap: 1.25, alignContent: 'start' }}>
      <Box sx={{ minHeight: 22, display: 'flex', alignItems: 'center' }}>{block('28%', 14)}</Box>
      {block('70%', 32)}
      <Box sx={{ py: '4px' }}>{block('100%', 8)}</Box>
      {block('85%', 18)}
    </Box>
  );
};

export interface BalancesCardProps {
  year: number;
  todayYmd: string;
  dayOfYear: number;
  daysInYear: number;
  percentComplete: number;
  updatedLabel?: string | null;
  pto: AllocationPace;
  sick: AllocationPace;
  combined: AllocationPace;
  loading: boolean;
}

const BalancesCard: React.FC<BalancesCardProps> = ({
  year,
  todayYmd,
  dayOfYear,
  daysInYear,
  percentComplete,
  updatedLabel,
  pto,
  sick,
  combined,
  loading,
}) => {
  const yearNote = `${formatDate(todayYmd)} is day ${dayOfYear} of ${daysInYear} (${formatPercent(percentComplete)} of the year)`;
  return (
    <SectionCard
      title={`PTO and sick time, ${year}`}
      description={updatedLabel || undefined}
      bodySx={{ containerType: 'inline-size', containerName: CARD_CONTAINER }}
    >
      {loading ? (
        <Box role="status" aria-label="Loading balances" aria-busy="true" sx={{ display: 'grid', gap: 3 }}>
          <span className="sbg-sr-only">Loading balances</span>
          <CombinedSkeleton />
          <Box sx={{ display: 'grid', columnGap: 4, rowGap: 3, ...PANEL_COLUMNS }}>
            <PanelSkeleton />
            <PanelSkeleton />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 3 }}>
          <CombinedBalance pace={combined} yearNote={yearNote} />
          {/* PTO and sick time as equal columns under the combined balance: neither outranks the other. */}
          <Box sx={{ display: 'grid', columnGap: 4, rowGap: 3, ...PANEL_COLUMNS }}>
            <BalancePanel title="PTO" pace={pto} />
            <BalancePanel title="Sick time" pace={sick} />
          </Box>
        </Box>
      )}
    </SectionCard>
  );
};

export default BalancesCard;
